import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningIcon from '@mui/icons-material/Warning';
import { usePlayer } from '../../context/PlayerContext';
import { useExpense } from '../../context/ExpenseContext';
import './ClearAllDataDialog.scss';

const ClearAllDataDialog = ({ open, onClose }) => {
  const { players, clearAllPlayers } = usePlayer();
  const { expenses, clearAllExpenses } = useExpense();
  const [confirmationText, setConfirmationText] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  
  const CONFIRMATION_TEXT = "DELETE ALL DATA";
  const totalPlayers = players.length;
  const totalExpenses = expenses.length;
  const hasData = totalPlayers > 0 || totalExpenses > 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (confirmationText !== CONFIRMATION_TEXT || !understood) {
      return;
    }

    setIsClearing(true);
    
    try {
      // Clear all data
      await clearAllPlayers();
      await clearAllExpenses();
      
      // Reset form state
      setConfirmationText('');
      setUnderstood(false);
      
      // Close dialog after a short delay to show success
      setTimeout(() => {
        setIsClearing(false);
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error clearing data:', error);
      setIsClearing(false);
    }
  };

  const handleClose = () => {
    if (isClearing) return; // Prevent closing during clearing process
    
    setConfirmationText('');
    setUnderstood(false);
    onClose();
  };

  const isValidForm = () => {
    return confirmationText === CONFIRMATION_TEXT && understood && !isClearing;
  };

  if (!hasData) {
    return (
      <Dialog 
        open={open} 
        onClose={handleClose} 
        className="clear-all-data-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="dialog-title">
          <Box className="title-content">
            <DeleteForeverIcon className="title-icon" />
            <Typography variant="h5">
              Clear All Data
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent className="dialog-content">
          <Alert severity="info" className="no-data-alert">
            <Typography variant="body1">
              There is no data to clear. Your application is already empty.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions className="dialog-actions">
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="inherit"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className="clear-all-data-dialog"
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle className="dialog-title">
          <Box className="title-content">
            <DeleteForeverIcon className="title-icon" />
            <Typography variant="h5">
              Clear All Data
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent className="dialog-content">
          <Box className="warning-section">
            <Alert severity="error" className="danger-alert">
              <Box className="warning-content">
                <WarningIcon className="warning-icon" />
                <Box>
                  <Typography variant="h6" className="warning-title">
                    DANGER ZONE
                  </Typography>
                  <Typography variant="body2">
                    This action cannot be undone. All data will be permanently deleted.
                  </Typography>
                </Box>
              </Box>
            </Alert>
            
            <Box className="data-summary">
              <Typography variant="body1" className="summary-title">
                The following data will be permanently deleted:
              </Typography>
              <ul className="data-list">
                <li>
                  <strong>{totalPlayers}</strong> players and all their buy-in/cash-out history
                </li>
                <li>
                  <strong>{totalExpenses}</strong> expense records
                </li>
                <li>
                  All settlement calculations and reports
                </li>
                <li>
                  All locally stored application data
                </li>
              </ul>
            </Box>
          </Box>
          
          <Box className="confirmation-section">
            <Typography variant="body1" className="confirmation-instruction">
              To confirm this action, type <strong>"{CONFIRMATION_TEXT}"</strong> in the field below:
            </Typography>
            
            <TextField
              fullWidth
              label={`Type "${CONFIRMATION_TEXT}" to confirm`}
              variant="outlined"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              error={confirmationText.length > 0 && confirmationText !== CONFIRMATION_TEXT}
              helperText={
                confirmationText.length > 0 && confirmationText !== CONFIRMATION_TEXT
                  ? "Text does not match. Please type exactly as shown."
                  : ""
              }
              placeholder={CONFIRMATION_TEXT}
              className="confirmation-input"
              disabled={isClearing}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                  color="error"
                  disabled={isClearing}
                />
              }
              label="I understand that this action is permanent and cannot be undone"
              className="checkbox-control"
            />
          </Box>
          
          {isClearing && (
            <Alert severity="warning" className="clearing-alert">
              <Typography variant="body2">
                Clearing all data... Please do not close this window.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions className="dialog-actions">
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            disabled={isClearing}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            color="error"
            disabled={!isValidForm()}
            startIcon={<DeleteForeverIcon />}
          >
            {isClearing ? 'Clearing...' : 'Delete All Data'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClearAllDataDialog;